import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import NeuralBackground from '../Shared/NeuralBackground';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { login, register } from '../../api/auth';
import 'react-toastify/dist/ReactToastify.css';

import './AuthForm.css';

import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaHome,
  FaExclamationTriangle,
  FaRocket,
  FaShieldAlt,
  FaChartLine,
  FaGlobe,
  FaCheckCircle
} from 'react-icons/fa';
import { RiRobot2Fill } from 'react-icons/ri';
import firstNationsEagleBg from '../../assets/first_nations_eagle_symbol.png'; // Eagle Symbol Background

const REGISTRATION_OPEN = false; // 🔒 Registration closed

const AuthForm = () => {
  const { setAuthData, authData } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (authData) {
      navigate(authData.role === 'admin' ? '/dashboard' : '/chat');
    }
  }, [authData, navigate]);

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setForm({ name: '', email: '', password: '' });
    setFormErrors({});
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return !value.trim() ? 'Full name is required.' : '';
      case 'email':
        if (!value) return 'Email is required.';
        if (!/\S+@\S+\.\S+/.test(value)) return 'Enter a valid email address.';
        return '';
      case 'password':
        return !value ? 'Password is required.' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    const error = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const errors = {
      email: validateField('email', form.email),
      password: validateField('password', form.password),
    };

    if (!isLogin) {
      errors.name = validateField('name', form.name);
    }

    setFormErrors(errors);
    return Object.values(errors).every((msg) => !msg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // 🛑 Block registration when closed
    if (!isLogin && !REGISTRATION_OPEN) {
      toast.info('Registration is temporarily closed.');
      return;
    }

    try {
      const res = isLogin
        ? await login({ email: form.email, password: form.password })
        : await register(form);

      localStorage.setItem('token', res.data.token || '');
      setAuthData(res.data.user);
      toast.success(`${isLogin ? 'Login' : 'Registration'} successful!`);
      navigate(res.data.user?.role === 'admin' ? '/dashboard' : '/chat');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
    }
  };

  return (
    <div className="auth-wrapper">
      {/* LEFT SIDE: Clean, Dark Form Area */}
      <div className="auth-left">
        <div className="auth-form-container">
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-logo">
              <RiRobot2Fill size={60} className="chatbot-logo" />
            </div>

            <h2>
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="auth-subtitle">
              {isLogin ? 'Enter your details to access your workspace.' : 'Get started with your AI journey today.'}
            </p>

            {!isLogin && (
              <div className={`input-icon ${formErrors.name ? 'has-error' : ''}`}>
                <FaUser className="icon" />
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={form.name}
                  onChange={handleChange}
                  disabled={!REGISTRATION_OPEN}
                />
                {formErrors.name && <p className="error-message">{formErrors.name}</p>}
              </div>
            )}

            <div className={`input-icon ${formErrors.email ? 'has-error' : ''}`}>
              <FaEnvelope className="icon" />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={form.email}
                onChange={handleChange}
                disabled={!isLogin && !REGISTRATION_OPEN}
              />
              {formErrors.email && <p className="error-message">{formErrors.email}</p>}
            </div>

            <div className={`input-icon ${formErrors.password ? 'has-error' : ''}`}>
              <FaLock className="icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                disabled={!isLogin && !REGISTRATION_OPEN}
              />
              <span
                className="toggle-password"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
              {formErrors.password && <p className="error-message">{formErrors.password}</p>}
            </div>

            <div className="form-actions">
              {/* Forgot Password link could go here */}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={!isLogin && !REGISTRATION_OPEN}
            >
              {isLogin ? 'Sign In' : 'Sign Up'}
            </button>

            {!isLogin && !REGISTRATION_OPEN && (
              <div className="registration-note">
                <FaExclamationTriangle />
                <span>Registration is currently invite-only.</span>
              </div>
            )}

            <div className="auth-footer">
              <p>
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <span onClick={toggleMode} className="toggle-auth">
                  {isLogin ? 'Sign up' : 'Sign in'}
                </span>
              </p>
            </div>

            <div className="back-home-link">
              <a href="/" className="home-link">
                <FaHome /> Back to Home
              </a>
            </div>
          </form>
        </div>
      </div>

      {/* RIGHT SIDE: Rich Visuals */}
      <div className="auth-right" style={{
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.3), rgba(15, 23, 42, 0.3)), url(${firstNationsEagleBg})`,
        backgroundSize: 'cover', // Full width coverage
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#1e1b4b' // Dark indigo background matching theme
      }}>
        <NeuralBackground /> {/* Keep for subtle animation effect on top */}
        <div className="auth-info-content">
          <h1>Unlock the Power of <br /> <span className="highlight-text">RAG Intelligence</span></h1>
          <p className="info-desc">
            Experience the next generation of conversational AI. Upload documents, ask complex questions, and get instant, accurate citations from your own data.
          </p>

          <div className="feature-list">
            <div className="feature-item">
              <div className="feature-icon"><FaRocket /></div>
              <div className="feature-text">
                <h3>Instant Answers</h3>
                <p>Real-time streaming responses directly from your knowledge base.</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon"><FaShieldAlt /></div>
              <div className="feature-text">
                <h3>Secure & Private</h3>
                <p>Enterprise-grade security for your sensitive data.</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon"><FaChartLine /></div>
              <div className="feature-text">
                <h3>Smart Analysis</h3>
                <p>Deep insights and patterns from your document repository.</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon"><FaCheckCircle /></div>
              <div className="feature-text">
                <h3>No Hallucinations</h3>
                <p>Accurate results with cited sources and direct document references.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
